import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";

const empty = { title: "", description: "", image_url: "", start_date: "", end_date: "", active: true };

export default function AdminCampaigns() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/campaigns")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/campaigns/${editing}`, form);
      else await api.post("/campaigns", form);
      toast.success("Guardat"); setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-campaigns">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline text-accent">Marketing</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Campanyes</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="campaign-new-btn"><Plus className="w-4 h-4 mr-2" /> Nova</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nova"} campanya</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Títol</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="campaign-form-title" /></div>
              <div><Label>Descripció</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>URL imatge</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Inici</Label><Input type="date" value={form.start_date?.slice(0, 10)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><Label>Fi</Label><Input type="date" value={form.end_date?.slice(0, 10)} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Activa</Label></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="campaign-form-save">Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <Card key={c.id} className="rounded-2xl border-2 overflow-hidden" data-testid={`campaign-card-${c.id}`}>
            {c.image_url && <img src={c.image_url} alt="" className="aspect-video w-full object-cover" />}
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                <span className={`px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-muted"}`}>{c.active ? "Activa" : "Inactiva"}</span>
              </div>
              <div className="font-heading font-semibold text-lg mt-2">{c.title}</div>
              <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{c.description}</div>
              <div className="text-xs text-muted-foreground mt-2">{c.start_date?.slice(0, 10)} → {c.end_date?.slice(0, 10)}</div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditing(c.id); setForm(c); setOpen(true); }}><Pencil className="w-3.5 h-3.5 mr-1" /> Edita</Button>
                <Button size="sm" variant="ghost" onClick={async () => { if (window.confirm("Segur?")) { await api.delete(`/campaigns/${c.id}`); load(); toast.success("Eliminat"); } }} data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-muted-foreground text-sm">Sense campanyes.</div>}
      </div>
    </div>
  );
}
